import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import ConsoleTabs, { panelProps } from '../../components/ConsoleTabs.jsx';
import PageHeader from '../../components/PageHeader.jsx';
import Notice from '../../components/Notice.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import KpiCard, { KpiGrid } from '../../components/KpiCard.jsx';
import { localDayKey } from '../../lib/score-history.js';
import { activityPath, formatLag, formatLocalWhen, formatWhen, notificationsPath } from '../../lib/watch-format.js';
import { buildFeedRows } from '../../lib/feed-rows.js';
import EventsFeed from './EventsFeed.jsx';
import WatchCases, { WatchCaseDetail } from './WatchCases.jsx';

// Activity (ENTRAID-347 redesign, mockup 4, implementation plan C3): what changed in the tenant, as onGuard Watch and
// the daily collection saw it. Moved out of Notifications: Notifications now only configures who is told what.
//   PageHeader "Activity" with "Notification settings" as its secondary action;
//   4 KPI cards (Live watch, Unanswered critical, Flagged open, Events over 7 days with one tile per day);
//   tabs Events and Flagged, addressed by the path (activity, activity/flagged, activity/flagged/:caseId).
// `operatorAccount` is the signed-in user's own operator flag (not the view-as claim): an operator gets Watch health
// and the daily collection emails only, and no Watch content call is made for them because the portal refuses it.

const DAY = 86_400_000;
const NONE = [];

// The collector sends a signed heartbeat every 15 minutes while Watch is on; two missed runs is silence.
const HEARTBEAT_FRESH_MS = 30 * 60_000;

/**
 * Where Watch stands. The saved setting and the collector's heartbeat are two different facts:
 *   off        the setting is off (nothing is expected from the collector);
 *   suspended  an operator holds the kill switch;
 *   turning-on the setting is saved on and no heartbeat has arrived since it was saved (up to 15 minutes);
 *   on         a heartbeat arrived within the last 30 minutes;
 *   silent     it was on and no heartbeat arrived in the last 30 minutes.
 */
export function watchPhase(w, now = Date.now()) {
  if (!w) return 'unknown';
  if (w.suspended) return 'suspended';
  if (w.enabled !== true) return 'off';
  const beat = Date.parse(w.lastWatchCallbackAt ?? '');
  if (Number.isFinite(beat) && now - beat <= HEARTBEAT_FRESH_MS) return 'on';
  const savedAt = Date.parse(w.settings?.enabledAt ?? '');
  const beatSinceSaved = Number.isFinite(beat) && (!Number.isFinite(savedAt) || beat >= savedAt);
  return beatSinceSaved ? 'silent' : 'turning-on';
}

// ENTRAID-346: the risk read reports its own status; 'ok' and 'skipped' need no line.
const RISK_READ_LABEL = Object.freeze({
  unlicensed: 'Off: needs Microsoft Entra ID P2',
  disabled: 'Turned off',
  failed: 'Unavailable',
  partial: 'Catching up',
});
const PHASE_LABEL = Object.freeze({ unknown: 'Unknown', suspended: 'Suspended', off: 'Off', 'turning-on': 'Turning on', on: 'Live', silent: 'No recent check' });
const PHASE_TONE = Object.freeze({ on: 'good', 'turning-on': 'warn', silent: 'bad', suspended: 'warn' });
const PHASE_LINE = Object.freeze({
  on: 'Live watch is on',
  off: 'Live watch is off.',
  suspended: 'Live watch is suspended by IPC Global.',
  'turning-on': 'Live watch is turning on.',
  silent: 'Live watch has not checked in the last 30 minutes.',
  unknown: '',
});

const messageOf = (e, fallback) => (e && typeof e.message === 'string' && e.message) || fallback;
const minutesAgo = (iso, now = Date.now()) => {
  const t = Date.parse(iso ?? '');
  if (!Number.isFinite(t)) return null;
  const m = Math.max(0, Math.round((now - t) / 60_000));
  return m < 1 ? 'less than a minute ago' : `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
};

const TABS = Object.freeze([{ id: 'events', label: 'Events', sub: '' }, { id: 'flagged', label: 'Flagged', sub: 'flagged' }]);

/** The Watch reads of the page: health for everyone, events and open cases for the customer's admins only. */
function useActivityData(customerId, operatorAccount) {
  const [watch, setWatch] = useState(null);
  const [watchError, setWatchError] = useState(null);
  const [events, setEvents] = useState(null);
  const [eventsMore, setEventsMore] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [openCases, setOpenCases] = useState(null);
  const [casesError, setCasesError] = useState(null);
  const [history, setHistory] = useState(NONE);
  const [historyError, setHistoryError] = useState(null);
  const [catalog, setCatalog] = useState(NONE);

  // Each read loads on its own: a failed one shows its error in its own place and never hides the others.
  useEffect(() => {
    let live = true;
    setWatch(null); setWatchError(null); setHistory(NONE); setHistoryError(null);
    api.getWatch(customerId).then(
      (w) => { if (live) setWatch(w); },
      (e) => { if (live) setWatchError(messageOf(e, 'Watch information could not be loaded.')); },
    );
    Promise.resolve().then(() => api.listNotificationHistory(customerId)).then(
      (h) => { if (live) setHistory(h?.history ?? NONE); },
      (e) => { if (live) setHistoryError(messageOf(e, 'The daily collection emails could not be loaded.')); },
    );
    Promise.resolve().then(() => api.listNotifications(customerId)).then(
      (c) => { if (live) setCatalog(c?.catalog ?? NONE); },
      () => {},
    );
    return () => { live = false; };
  }, [customerId]);

  useEffect(() => {
    setEvents(null); setEventsMore(false); setEventsError(null); setOpenCases(null); setCasesError(null);
    if (operatorAccount) return undefined;
    let live = true;
    api.listWatchEvents(customerId, { from: new Date(Date.now() - 30 * DAY).toISOString() }).then(
      (ev) => { if (live) { setEvents(ev.items ?? []); setEventsMore(Boolean(ev.cursor)); } },
      (e) => { if (live) setEventsError(messageOf(e, 'Watch events could not be loaded.')); },
    );
    api.listWatchCases(customerId, { state: 'open' }).then(
      (cases) => {
        if (!live) return;
        // One page holds at most 50 cases; a cursor means there are more.
        const n = (cases.items ?? []).length;
        setOpenCases(cases.cursor ? `${n}+` : n);
      },
      (e) => { if (live) setCasesError(messageOf(e, 'Open flagged could not be loaded.')); },
    );
    return () => { live = false; };
  }, [customerId, operatorAccount]);

  return { watch, watchError, events, eventsMore, eventsError, openCases, casesError, history, historyError, catalog };
}

/** Unanswered critical directory events of the last 7 days (admin notices excluded): the Activity badge count too. */
export function unansweredCritical(events, now = Date.now()) {
  return (events ?? []).filter((e) => e.noticeClass !== 'admin-notice'
    && e.severity === 'critical' && e.answerState === 'unanswered' && now - Date.parse(e.activityDateTime) <= 7 * DAY);
}

/** One tile per day of the last 7 (oldest first): 'on' when the day had a directory event. */
export function eventDays(events, now = Date.now()) {
  const days = new Set((events ?? []).filter((e) => e.noticeClass !== 'admin-notice').map((e) => localDayKey(e.activityDateTime)));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    return days.has(localDayKey(d)) ? 'on' : 'off';
  });
}

function CaseRedirect({ customerId }) {
  const { caseId } = useParams();
  return <Navigate to={activityPath(customerId, `flagged/${encodeURIComponent(caseId)}`)} replace />;
}

export default function ActivityPage({ customerId, operatorAccount }) {
  const data = useActivityData(customerId, operatorAccount);
  const location = useLocation();
  const navigate = useNavigate();
  const { watch, events, eventsMore, eventsError, history, historyError } = data;

  // Admin notices (changes to Watch itself) are records, not directory events: they sit in their own collapsed group
  // below the events and take no part in the filters or the counts (ENTRAID-289).
  const all = events ?? NONE;
  const adminNotices = useMemo(() => all.filter((e) => e.noticeClass === 'admin-notice'), [all]);
  const feedRows = useMemo(() => buildFeedRows({ events: operatorAccount ? [] : all, history }), [all, history, operatorAccount]);
  const ruleLabel = (id) => data.catalog.find((e) => e.id === id)?.label ?? id;

  const notInPlan = watch?.tierAllowed === false;
  const phase = watchPhase(watch);
  const lastCheck = minutesAgo(watch?.lastWatchCallbackAt);
  const subline = `What changed in your tenant, as onGuard Watch saw it. ${notInPlan ? 'Live watch is not part of this plan.' : (phase === 'on' ? `${PHASE_LINE.on}${lastCheck ? `, last check ${lastCheck}` : ''}.` : PHASE_LINE[phase])}`.trim();

  const tabs = operatorAccount ? TABS.slice(0, 1) : TABS;
  const base = activityPath(customerId);
  const first = location.pathname.startsWith(base) ? location.pathname.slice(base.length).replace(/^\/+/, '').split('/')[0] : '';
  const active = tabs.find((t) => t.sub === first) ?? tabs[0];
  const counts = { events: feedRows.length, flagged: typeof data.openCases === 'number' ? data.openCases : null };

  const settings = (
    <Link to={notificationsPath(customerId, 'rules')} className="btn-secondary gap-2">
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
      Notification settings
    </Link>
  );

  const events7 = all.filter((e) => e.noticeClass !== 'admin-notice' && Date.now() - Date.parse(e.activityDateTime) <= 7 * DAY);
  const feed = notInPlan && !operatorAccount ? null : (
    <SectionCard ariaLabel="Events" testId="activity-events">
      <EventsFeed customerId={customerId} rows={feedRows} loading={!operatorAccount && !events && !eventsError} error={operatorAccount ? historyError : (eventsError ?? historyError)} ruleLabel={ruleLabel} />
      {eventsMore ? <p className="pt-3 text-[13px] text-ipc-muted">More Watch events exist in this period; the list shows the most recent ones.</p> : null}
      {!operatorAccount && adminNotices.length > 0 ? (
        <details className="mt-3 border-t border-ipc-border pt-3" data-testid="admin-notices">
          <summary className="cursor-pointer text-sm font-semibold text-ipc-ink">Watch configuration changes ({adminNotices.length})</summary>
          <ul className="mt-2 divide-y divide-ipc-border">
            {adminNotices.map((e) => (
              <li key={e.eventId} className="py-2">
                <Link className="break-words text-sm font-semibold text-ipc-light hover:underline" to={activityPath(customerId, `events/${encodeURIComponent(e.eventId)}`)}>{e.what}</Link>
                <div className="text-[12.5px] text-ipc-muted">{formatWhen(e.activityDateTime)}</div>
              </li>
            ))}
          </ul>
        </details>
      ) : (!operatorAccount && events && feedRows.length > 0 ? <p className="pt-3 text-[13px] text-ipc-muted">Showing the last 30 days.</p> : null)}
    </SectionCard>
  );

  return (
    <div>
      <PageHeader title="Activity" subline={subline} secondary={settings} />
      <div className="space-y-4">
        <ActivityKpis data={data} phase={phase} notInPlan={notInPlan} operatorAccount={operatorAccount} events7={events7} customerId={customerId} />
        {operatorAccount ? <p className="text-sm text-ipc-muted">Watch content is visible to the customer&apos;s onGuard administrators only.</p> : null}
        {tabs.length > 1 ? (
          <ConsoleTabs
            label="Activity"
            idPrefix="activity"
            items={tabs.map((t) => ({ id: t.id, label: counts[t.id] != null ? `${t.label} ${counts[t.id]}` : t.label }))}
            value={active.id}
            onChange={(id) => navigate(activityPath(customerId, tabs.find((t) => t.id === id).sub))}
          />
        ) : null}
        <div {...(tabs.length > 1 ? panelProps('activity', active.id) : {})}>
          <Routes>
            <Route index element={feed} />
            {operatorAccount ? null : <Route path="flagged" element={<WatchCases customerId={customerId} />} />}
            {operatorAccount ? null : <Route path="flagged/:caseId" element={<WatchCaseDetail customerId={customerId} />} />}
            {operatorAccount ? null : <Route path="cases" element={<Navigate to={activityPath(customerId, 'flagged')} replace />} />}
            {operatorAccount ? null : <Route path="cases/:caseId" element={<CaseRedirect customerId={customerId} />} />}
            <Route path="*" element={<Navigate to={base} replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function ActivityKpis({ data, phase, notInPlan, operatorAccount, events7, customerId }) {
  const { watch, watchError, events, eventsMore, eventsError, openCases, casesError } = data;
  if (watchError) return <Notice tone="bad" title="Watch information could not be loaded">{watchError}</Notice>;
  if (!watch) return <div role="status" className="text-sm text-ipc-muted">Loading Watch</div>;
  if (notInPlan || phase === 'off') {
    return (
      <Notice
        tone="info"
        title={notInPlan ? 'Watch is not part of this plan.' : 'Watch is off.'}
        action={phase === 'off' && !notInPlan && !operatorAccount ? <Link to={notificationsPath(customerId, 'rules')} className="text-sm font-semibold text-ipc-light hover:underline">Turn it on in Notifications</Link> : null}
        testId="watch-off"
      >
        {notInPlan ? null : 'The daily collection emails still show below.'}
      </Notice>
    );
  }
  const lag = watch.health?.lagSeconds;
  const healthStatus = typeof watch.health?.status === 'string' ? watch.health.status : null;
  const riskStatus = typeof watch.health?.riskRead?.status === 'string' ? watch.health.riskRead.status : null;
  const riskLabel = riskStatus && Object.hasOwn(RISK_READ_LABEL, riskStatus) ? RISK_READ_LABEL[riskStatus] : null;
  const phaseCaption = phase === 'turning-on'
    ? 'Saved as on. Waiting for the first collector run (up to 15 minutes).'
    : phase === 'silent' ? 'No collector check in the last 30 minutes'
      : (watch.deployed === false ? (watch.deploying === true ? 'Watch function being installed in your AWS account (a few minutes)' : 'Watch function installed when Live watch is first turned on')
        : `Last check ${watch.lastWatchCallbackAt ? formatLocalWhen(watch.lastWatchCallbackAt) : 'never'}, lag ${formatLag(lag)}`);

  const unanswered = unansweredCritical(events);
  const newest = [...unanswered].sort((a, b) => Date.parse(b.activityDateTime) - Date.parse(a.activityDateTime))[0];
  const confirmed = events7.filter((e) => e.answerState === 'yes').length;
  // Attribution (Mark as expected) and aging out are shown apart; aged out is never counted as confirmed.
  const byAdmin = events7.filter((e) => e.answerState === 'yes' && e.markedExpected).length;
  const agedOut = events7.filter((e) => e.answerState === 'aged-out').length;
  const eventsCaption = `${confirmed} confirmed as expected${byAdmin ? ` (${byAdmin} answered by an admin)` : ''}${agedOut ? `, ${agedOut} aged out` : ''}`;
  const more = eventsMore ? '+' : '';

  return (
    <>
      <KpiGrid max={operatorAccount ? 3 : 4} data-testid="activity-kpis">
        <KpiCard label="Live watch" value={PHASE_LABEL[phase]} tone={PHASE_TONE[phase]} caption={phaseCaption} testId="tile-watch" />
        {operatorAccount ? (
          <>
            <KpiCard label="Last check" value={watch.lastWatchCallbackAt ? formatLocalWhen(watch.lastWatchCallbackAt) : 'Never'} testId="tile-last-check" />
            <KpiCard label="Lag" value={formatLag(lag)} caption="Time from a change in Microsoft Entra ID to onGuard seeing it" testId="tile-lag" />
          </>
        ) : (
          <>
            <KpiCard
              label="Unanswered critical"
              value={eventsError ? null : (events ? `${unanswered.length}${more}` : null)}
              unit="last 7 days"
              tone={unanswered.length > 0 ? 'warn' : undefined}
              caption={eventsError ? 'Unavailable' : (newest ? `${newest.what} ${formatLocalWhen(newest.activityDateTime)}` : (events ? 'Nothing waiting for an answer' : null))}
              to={unanswered.length > 0 ? activityPath(customerId) : undefined}
              ariaLabel={unanswered.length > 0 ? `Unanswered critical: ${unanswered.length}${more} in the last 7 days. Open the events` : undefined}
              testId="tile-unanswered-critical"
            />
            <KpiCard
              label="Flagged open"
              value={casesError ? null : openCases}
              caption={casesError ? `Unavailable: ${casesError}` : (openCases === 0 ? 'No open case' : 'Cases still being followed up')}
              to={activityPath(customerId, 'flagged')}
              ariaLabel={`Flagged open: ${casesError ? 'unavailable' : (openCases ?? 'loading')}. Open Flagged`}
              testId="tile-open-cases"
            />
            <KpiCard
              label="Events"
              kpi="Events, last 7 days"
              value={eventsError ? null : (events ? `${events7.length}${more}` : null)}
              unit="last 7 days"
              variant="tiles"
              tileStates={eventDays(events)}
              caption={events ? eventsCaption : null}
              testId="tile-events"
            />
          </>
        )}
      </KpiGrid>
      {healthStatus && healthStatus !== 'ok' ? <Notice tone="warn" title={`Watch health: ${healthStatus}`} titleAs="p" testId="tile-health" /> : null}
      {riskLabel ? <Notice tone="info" title={`Risky sign-ins: ${riskLabel}`} titleAs="p" testId="tile-risk" /> : null}
    </>
  );
}
